import { QuestionType, QuestionFormat, QuestionReportType, QuestionOptionsDisplay, type Question } from '../SurveysTypes.d';

// Groups children under their parent explicitly instead of relying on the API returning them
// already adjacent to it - matrix rows aren't guaranteed to stay contiguous with their header.
export function orderQuestionsWithChildren<T extends Question>(questions: T[]): T[] {
    const childrenByParent = new Map<string, T[]>();
    const topLevelQuestions: T[] = [];
    for (const question of questions) {
        if (question.ParentQuestionId) {
            const siblings = childrenByParent.get(question.ParentQuestionId) ?? [];
            siblings.push(question);
            childrenByParent.set(question.ParentQuestionId, siblings);
        } else {
            topLevelQuestions.push(question);
        }
    }

    const orderedQuestions: T[] = [];
    for (const question of topLevelQuestions) {
        orderedQuestions.push(question, ...(childrenByParent.get(question.QuestionId) ?? []));
    }

    const topLevelQuestionIds = new Set(topLevelQuestions.map((q) => q.QuestionId));
    const orphanChildren = questions.filter((q) => q.ParentQuestionId && !topLevelQuestionIds.has(q.ParentQuestionId));
    orderedQuestions.push(...orphanChildren);

    return orderedQuestions;
}

export function questionTypeLabel(q: Question): string {
    if (q.Type === QuestionType.Hidden) return $Resources.QuestionChoiceTypeHiddenLabel;
    if (q.Type === QuestionType.Single && q.ReportType === QuestionReportType.Nps) return $Resources.QuestionChoiceTypeNpsLabel;
    if (q.Type === QuestionType.Single && q.ReportType === QuestionReportType.Csat) return $Resources.CSATInfoHeading;
    if (q.Type === QuestionType.Single && q.ReportType === QuestionReportType.CustomBool) return $Resources.QuestionChoiceTypeBoolLabel;
    if (q.Type === QuestionType.Single && q.OptionsDisplay === QuestionOptionsDisplay.Dropdown) return $Resources.OptionsDisplayDropdownLabel;
    if (q.Type === QuestionType.Single) return $Resources.QuestionChoiceTypeSingleLabel;
    if (q.Type === QuestionType.Multiple) return $Resources.QuestionChoiceTypeMultipleLabel;
    if (q.Type === QuestionType.MultipleNumeric) return $Resources.QuestionChoiceTypeMultipleNumericLabel;
    if (q.Type === QuestionType.InputField && q.Format === QuestionFormat.Number) return $Resources.QuestionFormatNumberLabel;
    if (q.Type === QuestionType.InputField) return $Resources.QuestionTypeInputFieldLabel;
    if (q.Type === QuestionType.FreeText) return $Resources.QuestionTypeFreeTextLabel;
    if (q.Type === QuestionType.FileUpload) return $Resources.QuestionTypeFileUploadLabel;
    if (q.Type === QuestionType.EventParticipant) return $Resources.QuestionBlockEventParticipantName;
    if (q.Type === QuestionType.Attestation) return $Resources.QuestionBlockAttestation;
    if (q.Type === QuestionType.Matrix) return $Resources.QuestionTypeMatrixLabel;
    return '';
}

// Mirrors the knockout.ungapped.js `parseNumberInputFormatExpressionOptions` binding handler,
// which encodes a Number-formatted InputField's min/max range as "minmax:[min,max]" or "slider:[min,max]".
export function questionNumberMinMax(q: Question): { min: number; max: number } | null {
    if (q.Type !== QuestionType.InputField || q.Format !== QuestionFormat.Number) return null;

    const expression = q.FormatExpression ?? '';
    if (!/slider|minmax/.test(expression)) return null;

    const [min, max] = expression.replace(/^(?:slider|minmax):\[|\]$/g, '').split(',');
    return { min: parseInt(min, 10) || 0, max: parseInt(max, 10) || 100 };
}

export function questionMaxOptionsLabel(q: Question): string | null {
    if (q.Type !== QuestionType.Multiple || q.MaxOptions === null) return null;
    return $Resources.SurveyResultsMaxOptionsAllowedLabel?.replace('{{MaxOptions}}', String(q.MaxOptions));
}
